import React, { useState, useEffect } from 'react';
import { Slide } from '../types';
import { slideApiService } from '../services/slideApiService';
import { useFavorites } from '../contexts/FavoritesContext';
import AuthenticatedImage from './AuthenticatedImage';
import FavoriteButton from './FavoriteButton';

/**
 * Adds the user's current favorites into a collection, opened from the collection detail.
 * Mirrors the iOS AddFavoritesToCollectionSheet. Shows the current favorites as cards with the
 * star; un-starring a card removes it from favorites (the star's normal behavior) but the card
 * stays (dimmed) so a mis-tap can be undone. "Add N" adds only the still-favorited slides;
 * favorites are NOT cleared (this adds to an existing collection).
 */
interface AddFavoritesToCollectionModalProps {
  shopId: string;
  collectionId: string;
  collectionName: string;
  onAdded: (count: number) => void; // parent shows toast + reloads + closes
  onClose: () => void;
}

const BORDER = 'var(--border-light)';
const MUTED = 'var(--text-accent)';
const TEXT = 'var(--text-accent)';

const AddFavoritesToCollectionModal: React.FC<AddFavoritesToCollectionModalProps> = ({
  shopId, collectionId, collectionName, onAdded, onClose,
}) => {
  const { favoriteIds, isFavorite } = useFavorites();
  // Snapshot the favorite IDs at open so cards stay even after the user un-stars one.
  const [snapshotIds] = useState<string[]>(() => Array.from(favoriteIds).sort());
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (snapshotIds.length === 0) { setLoading(false); return; }
      try {
        const hydrated = await slideApiService.batchGetSlides(shopId, snapshotIds);
        if (!cancelled) setSlides(hydrated);
      } catch (err) {
        console.error('Failed to load favorites:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shopId, snapshotIds]);

  // Snapshot slides that are still favorited — i.e. what will be added.
  const selectedIds = slides.filter(s => isFavorite(s.id)).map(s => s.id);

  const handleAdd = async () => {
    if (selectedIds.length === 0) return;
    try {
      setAdding(true);
      await slideApiService.addSlidesToCollection(shopId, collectionId, selectedIds);
      onAdded(selectedIds.length);
    } catch (err) {
      console.error('Failed to add favorites to collection:', err);
      setAdding(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'var(--bg-card)', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '560px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: '1.125rem', fontWeight: 600, color: TEXT }}>Add Favorites</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.875rem', color: MUTED }}>
          Tap a star to drop a slide before adding. Favorites are kept after adding.
        </p>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: MUTED }}>
              <div className="spinner-sm mx-auto mb-2" />
              Loading…
            </div>
          ) : slides.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: MUTED, fontSize: '0.875rem' }}>
              No favorites yet. Star slides on the Slides tab, then add them here.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: '8px' }}>
              {slides.map(slide => {
                const imageId = slide.primaryImage?.id || slide.images?.[0]?.id;
                const fav = isFavorite(slide.id);
                return (
                  <div
                    key={slide.id}
                    style={{
                      position: 'relative', aspectRatio: '1 / 1', borderRadius: '8px', overflow: 'hidden',
                      border: `1px solid ${BORDER}`, backgroundColor: 'var(--bg-secondary)',
                      opacity: fav ? 1 : 0.5, transition: 'opacity 0.15s ease',
                    }}
                  >
                    {imageId && (
                      <AuthenticatedImage
                        shopId={shopId}
                        slideId={slide.id}
                        imageId={imageId}
                        alt={slide.name}
                        size="thumb_300"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                    <FavoriteButton slideId={slide.id} size={22} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleAdd}
            disabled={selectedIds.length === 0 || adding}
          >
            {adding ? 'Adding…' : `Add ${selectedIds.length} to "${collectionName}"`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddFavoritesToCollectionModal;
